import { useMutation } from '@tanstack/react-query'
import queryClient from '@/queryClient'
import { useCadPageParams } from '@/pages/CADPage/hooks'
import { deleteDocumentPageAssemblyGroupID } from '@/lib/api/client'
import { QUERY_KEYS as DOCUMENT_PAGES_QUERY_KEYS } from '@/services/queries/document_pages/queryKeys'
import { useCADPageStore } from '@/state/cad'
import { QUERY_KEYS as VIEWS_QUERY_KEYS } from '../views'

import type { DocumentPage } from '@/lib/api/client'
import CustomError from '@/lib/api/CustomError'
import { useToast } from '@/components/ui/use-toast'
import { useToggleStepCardsList } from '@/pages/CADPage/components/AssemblyOrderPanel/StepCardList/hooks/useToggleStepCardsList'
import { useOperationSteps } from './useOperationSteps'
import { useKickViewWorker } from '@/services/hooks/view_worker'
import {
  getPartsTablesQuery,
  useUpdateTable,
} from '@/services/hooks/template_attributes'

export const useDeletePartsFromOperation = () => {
  const { documentId } = useCadPageParams()
  const { steps } = useOperationSteps()
  const { toast } = useToast()
  const activeStep = useCADPageStore((state) => state.operationStep)
  const getCadPageState = useCADPageStore((state) => state.getState)
  const setCadPageState = useCADPageStore((state) => state.setState)
  const { isViewWorkerProcessing, viewWorker } = useKickViewWorker()
  const toggleStepCardsList = useToggleStepCardsList()
  const { mutateAsync: updateTable } = useUpdateTable()

  return useMutation({
    mutationFn: async ({
      cadVersionId,
      assemblyGroupIds,
      documentPageId,
      documentVersionId,
      documentPageAssemblyReferenceIds,
      partsIndex,
    }: {
      cadVersionId: string
      assemblyGroupIds: Array<string>
      documentPageId: string
      documentVersionId: string
      documentPageAssemblyReferenceIds: Array<number>
      partsIndex?: Array<number>
    }) => {
      await Promise.all(
        documentPageAssemblyReferenceIds.map((id) =>
          deleteDocumentPageAssemblyGroupID(id),
        ),
      )
      const partsTables = await getPartsTablesQuery(documentPageId)

      if (partsIndex?.length) {
        const tablesWithCustomColumns = partsTables.filter(
          (table) => !!table.template_values?.columns?.length,
        )
        tablesWithCustomColumns.forEach((table) => {
          const rows = table.template_values?.rows || []
          const updatedRows = rows.filter(
            (_, index) => !partsIndex.includes(index),
          )

          void updateTable({
            templateAttributeId: table.id as string,
            documentPageId,
            tableData: {
              type: 'parts-table',
              rows: updatedRows,
            },
          })
        })
      }
      return {
        cadVersionId,
        assemblyGroupIds,
        documentPageId,
        documentVersionId,
      }
    },

    onSuccess: async ({
      cadVersionId,
      assemblyGroupIds,
      documentVersionId,
    }) => {
      await queryClient.invalidateQueries({
        queryKey: [
          DOCUMENT_PAGES_QUERY_KEYS.DOCUMENT_PAGES,
          { documentVersionId },
        ],
        refetchType: 'all',
      })

      await queryClient.invalidateQueries({
        queryKey: [VIEWS_QUERY_KEYS.VIEWS],
        refetchType: 'all',
      })

      const selectFromCad = activeStep?.selectFromCad
      if (activeStep && !selectFromCad) {
        const selectedParts = getCadPageState().selectedParts

        setCadPageState({
          selectedParts: selectedParts.filter(
            (id) => !assemblyGroupIds.includes(id),
          ),
          operationStep: {
            documentId,
            stepId: activeStep.stepId,
            isActive: false,
            selectFromCad: false,
          },
        })

        await toggleStepCardsList({
          steps,
          previousSteps: steps,
          documentVersionId,
          isReadOnly: false,
        })

        if (viewWorker && isViewWorkerProcessing) {
          await viewWorker.kick(cadVersionId, documentVersionId)
        }
      }
    },

    onMutate: ({ assemblyGroupIds, documentPageId, documentVersionId }) => {
      const queryKey = [
        DOCUMENT_PAGES_QUERY_KEYS.DOCUMENT_PAGES,
        { documentVersionId },
      ]

      const previousDocumentPages = (queryClient.getQueryData(queryKey) ||
        []) as DocumentPage[]

      queryClient.setQueryData(queryKey, (old: DocumentPage[]) => {
        if (!old) return []

        return old.map((doc) => {
          if (doc.id === documentPageId) {
            return {
              ...doc,
              documentpageassemblygroupids:
                doc.documentpageassemblygroupids.filter(
                  (id) => !assemblyGroupIds.includes(id.assembly_group_id),
                ),
            }
          }
          return doc
        })
      })

      return { previousDocumentPages }
    },

    onError: (error, { documentVersionId }, context) => {
      console.error('Failed to remove part from operation', error)

      queryClient.setQueryData(
        [DOCUMENT_PAGES_QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
        context?.previousDocumentPages || [],
      )

      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}
